/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, Inline, Stack, Box, Text } from 'sarsaparilla';
import cx from 'classnames';
import range from 'lodash/range';

import { gnoment } from 'shared-ui/gnoment';
import StickyTableHeaderHelper from 'ui-permit/shared-components/detailed-availability/StickyTableHeaderHelper';
import {
    Grid,
    Row,
    GridHeaderCell,
    GridCell,
} from 'ui-permit/src/components/detailed-availability-page-new/Grid';
import SortableTableColumnHeader from './detailed-availability-page/SortableTableColumnHeader';
import AvailabilityCell from './AvailabilityCell';
import {
    getSortedDivisions,
    getAvailability,
    getBWCAWWeekStart,
} from '../utils/permitUtilsNew';
import { computeWeekRange } from '../utils/issuanceUtils';
import { GROUP_AVAILABILITY_WEEKLY_KEY_NAME } from '../constants';

const propTypes = {
    divisions: PropTypes.array,
    permitDate: PropTypes.string,
    availabilities: PropTypes.object,
    selectedStartDate: PropTypes.instanceOf(gnoment),
    selectedEndDateDate: PropTypes.string,
    selectedDivision: PropTypes.object,
    onSelectToggle: PropTypes.func,
    onDivisionDetailsClick: PropTypes.func,
    isSmallerGrid: PropTypes.bool,
    children: PropTypes.node,
};

const defaultProps = {};
const NUM_OF_COLS_WEEKLY = 4;
const SORT_COLS_DISTRICT = ['district', 'name'];
const SORT_COLS_NAME = ['name'];

export default class WeeklyMotorAvailability extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            sort: {
                column: SORT_COLS_DISTRICT,
                isSorted: true,
                isAsc: true,
            },
        };
        this.state.divisions = getSortedDivisions(props.divisions, this.state.sort);
        this.onColumnSortToggle = this.onColumnSortToggle.bind(this);
        this.onDistrictSortToggle = this.onDistrictSortToggle.bind(this);
        this.onNameSortToggle = this.onNameSortToggle.bind(this);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.divisions !== this.props.divisions) {
            this.setState({
                divisions: getSortedDivisions(nextProps.divisions, this.state.sort),
            });
        }
    }

    onColumnSortToggle(columnName, isSorted, isAsc) {
        this.setState(
            {
                sort: {
                    column: columnName,
                    isSorted,
                    isAsc,
                },
            },
            () => {
                this.setState({
                    divisions: getSortedDivisions(this.state.divisions, this.state.sort),
                });
            }
        );
    }

    onNameSortToggle(isSorted, isAsc) {
        this.onColumnSortToggle(SORT_COLS_NAME, isSorted, isAsc);
    }

    onDistrictSortToggle(isSorted, isAsc) {
        this.onColumnSortToggle(SORT_COLS_DISTRICT, isSorted, isAsc);
    }

    onSelectToggle(division, index, count) {
        if (this.props.onSelectToggle) {
            if (index >= 0) {
                const { start: startDate, end: endDate } = computeWeekRange(
                    gnoment(this.props.permitDate).add(index, 'week')
                );
                this.props.onSelectToggle(
                    division,
                    startDate.startOf('day'),
                    endDate.startOf('day').toISOString(),
                    count
                );
            } else {
                this.props.onSelectToggle(division);
            }
        }
    }

    getWeekStartEnd(index) {
        const { start: weekStart, end: weekEnd } = computeWeekRange(
            gnoment(this.props.permitDate).add(index, 'week')
        );
        return { weekStart, weekEnd };
    }

    getDateHeader(index) {
        const { isSmallerGrid } = this.props;
        const { weekStart, weekEnd } = this.getWeekStartEnd(index);
        return (
            <GridHeaderCell key={index} aria-disabled="true">
                <Stack space="none">
                    {isSmallerGrid ? (
                        <>
                            <Box className="weekday">{weekStart.format('MMM DD')} - </Box>
                            <Box className="weekday">
                                {weekEnd.format('MMM DD')} &#32;
                            </Box>
                        </>
                    ) : (
                        <Box className="weekday">
                            {weekStart.format('MMM DD')} - {weekEnd.format('MMM DD')}
                        </Box>
                    )}
                    {!isSmallerGrid && (
                        <Inline space="xxs" alignX="center" className="days">
                            <Box>S</Box>
                            <Box>S</Box>
                            <Box>M</Box>
                            <Box>T</Box>
                            <Box>W</Box>
                            <Box>T</Box>
                            <Box>F</Box>
                        </Inline>
                    )}
                </Stack>
            </GridHeaderCell>
        );
    }

    getAvailabilityCell(division, index) {
        let isSelectedDivision = false;
        if (
            this.props.selectedDivision &&
            this.props.selectedDivision.id === division.id
        ) {
            isSelectedDivision = true;
        }
        const permitDate = gnoment(this.props.permitDate);
        const startOfWeek = getBWCAWWeekStart(
            permitDate.clone().add(index, 'week').startOf('day')
        );
        const isSelectedCell =
            isSelectedDivision &&
            startOfWeek.isSame(gnoment(this.props.selectedStartDate));

        let count = null;
        let total = null;
        let reserved = null;
        let weeklyCount = null;
        let weeklyTotal = null;

        const avail = getAvailability(
            this.props.availabilities,
            permitDate,
            division.id,
            startOfWeek,
            GROUP_AVAILABILITY_WEEKLY_KEY_NAME
        );
        if (avail) {
            count = avail.remaining;
            total = avail.total;
            weeklyCount = count;
            weeklyTotal = total;
            reserved = total - count;
        }

        let isSelectable = true;
        if (
            count === 0 ||
            startOfWeek.clone().add(7, 'day').isBefore(gnoment().startOf('day'))
        ) {
            isSelectable = false;
        }

        const { weekStart, weekEnd } = this.getWeekStartEnd(index);
        const dateLabel = `${weekStart.format('MMMM DD')} - ${weekEnd.format('MMMM DD')}`;

        return (
            <AvailabilityCell
                key={index}
                dateLabel={dateLabel}
                count={count}
                total={total}
                reserved={reserved}
                weeklyCount={weeklyCount}
                weeklyTotal={weeklyTotal}
                isSelected={isSelectedCell}
                isChecked={isSelectedCell}
                isSelectable={isSelectable}
                divisionName={division.name}
                onSelectToggle={this.onSelectToggle.bind(this, division, index, count)}
            />
        );
    }

    renderHeader() {
        return (
            <Row>
                <SortableTableColumnHeader
                    id="Sites"
                    name="Sites"
                    isSorted={
                        this.state.sort.column === SORT_COLS_NAME
                            ? this.state.sort.isSorted
                            : false
                    }
                    isAsc={
                        this.state.sort.column === SORT_COLS_NAME
                            ? this.state.sort.isAsc
                            : false
                    }
                    onSortToggle={this.onNameSortToggle}
                />
                {!this.props.isSmallerGrid && (
                    <SortableTableColumnHeader
                        id="Area"
                        name="Area"
                        isSorted={
                            this.state.sort.column === SORT_COLS_DISTRICT
                                ? this.state.sort.isSorted
                                : false
                        }
                        isAsc={
                            this.state.sort.column === SORT_COLS_DISTRICT
                                ? this.state.sort.isAsc
                                : false
                        }
                        onSortToggle={this.onDistrictSortToggle}
                    />
                )}
                {range(NUM_OF_COLS_WEEKLY).map((i) => {
                    return this.getDateHeader(i);
                })}
            </Row>
        );
    }

    renderRow(division) {
        const { isSmallerGrid } = this.props;

        const divisionName = (
            <Text>
                <Button
                    appearance="link"
                    onClick={() => this.props.onDivisionDetailsClick(division.id)}
                >
                    {division.name}
                </Button>
            </Text>
        );

        const divisionComponent = !isSmallerGrid ? (
            divisionName
        ) : (
            <>
                {divisionName}
                <Text>{division.district}</Text>
            </>
        );

        return (
            <Row
                key={division.id}
                className={cx({
                    'selected-row':
                        this.props.selectedDivision &&
                        this.props.selectedDivision.id === division.id,
                })}
            >
                <GridCell>
                    <div translate="no">{divisionComponent}</div>
                </GridCell>
                {!isSmallerGrid && (
                    <GridCell>
                        <div translate="no">{division.district}</div>
                    </GridCell>
                )}
                {range(NUM_OF_COLS_WEEKLY).map((i) => {
                    return this.getAvailabilityCell(division, i);
                })}
            </Row>
        );
    }

    render() {
        const { isSmallerGrid, isFullView, children } = this.props;

        return (
            <div className="per-availability-table-container weekly-availability">
                <StickyTableHeaderHelper
                    scrollingContainerId="per-availability-main"
                    isFullView={isFullView}
                >
                    {children}
                    <div aria-hidden="true" className="sticky-availability-table-header">
                        <Grid
                            className={cx('detailed-availability-grid-new', {
                                'has-area-data': !isSmallerGrid,
                                'mobile-app': isSmallerGrid,
                            })}
                        >
                            {this.renderHeader()}
                        </Grid>
                    </div>
                </StickyTableHeaderHelper>
                <Grid
                    className={cx('detailed-availability-grid-new', {
                        'has-area-data': !isSmallerGrid,
                        'mobile-app': isSmallerGrid,
                    })}
                    caption={'Availability by Sites and Dates'}
                >
                    {this.renderHeader()}

                    {this.state.divisions.map((division) => {
                        return this.renderRow(division);
                    })}
                </Grid>
            </div>
        );
    }
}

WeeklyMotorAvailability.propTypes = propTypes;
WeeklyMotorAvailability.defaultProps = defaultProps;
